import { useEffect, useMemo } from 'react';
import { OperationResult } from 'hooks/store/types';
import { useMatchEnterpriseAdmin, useMatchProjects } from '../../../navigation/matches';
import {
	ActionTypes,
	getProjects,
	selectInitialProjectIds,
	selectProjects
} from 'store/data/projects';
import { useDispatch, useSelector } from 'hooks/utils';
import { useActivity } from 'hooks/store/utils/useActivity';

interface Options {
	lazy?: boolean;
	checkRoute?: boolean;
}

/**
 * Fetches projects based on the current route and returns the projects that the user is a collaborator or owner of.
 * It's important to note we're filtering out projects that the user is not a collaborator or owner of, e.g. projects added to
 * redux state because an org admin views project collaborators.
 */
export function useScopedProjects(options?: Options): OperationResult<string[]> {
	const dispatch = useDispatch();

	const lazy = !!options?.lazy;
	const checkRoute = options?.checkRoute !== undefined ? options?.checkRoute : true;

	const matchProjects = useMatchProjects();
	const matchEnterpriseAdmin = useMatchEnterpriseAdmin();

	const areProjectsFetched = useSelector(state => state.data.projects.projects.fetched);

	const projectIds = useSelector(state => selectProjects(state.data.projects));

	const [{ loading: loadingProjects, error: projectsError }] = useActivity(
		ActionTypes.GET_PROJECTS
	);

	useEffect(() => {
		if (
			(matchProjects || matchEnterpriseAdmin) &&
			!projectsError &&
			!areProjectsFetched &&
			!loadingProjects &&
			!lazy
		) {
			dispatch(getProjects());
		}
	}, [matchProjects, projectsError, matchEnterpriseAdmin]);

	let loading = false;
	let error = false;
	let fetched = false;

	if (matchProjects || matchEnterpriseAdmin) {
		loading = loadingProjects;
		error = projectsError;
		fetched = areProjectsFetched;
	}

	function handler() {
		if (checkRoute) {
			if (matchProjects || matchEnterpriseAdmin) {
				dispatch(getProjects());
			}
		} else dispatch(getProjects());
	}

	/**
	 * initialProjectIds is here used as a proxy for "what projects the user is a collaborator or owner of",
	 * and used for hiding projects that the user is not a collaborator or owner of, e.g. projects added to
	 * redux state because an org admin views project collaborators.
	 */
	const initialProjectIds = useSelector(state => selectInitialProjectIds(state.data.projects));

	const projectIdsWhereUserIsOwnerOrCollaborator = useMemo(() => {
		if (!initialProjectIds || !Array.isArray(initialProjectIds)) {
			return [];
		}

		return projectIds.filter(projectId => initialProjectIds.includes(projectId));
	}, [projectIds, initialProjectIds]);

	return [{ loading, error, data: projectIdsWhereUserIsOwnerOrCollaborator, fetched }, handler];
}
